import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import {
  FaSearch,
  FaFilter,
  FaPlus,
  FaNewspaper,
  FaShareAlt,
  FaBookmark,
  FaSyncAlt,
  FaSortAmountDown,
  FaBrain,
  FaExternalLinkAlt,
  FaClock,
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

import { TrendChart, DistributionChart } from './components/Charts';

import {
  TechShell,
  TechPageHeader,
  TechSection,
  Grid,
  Span,
  TechInput,
  TechSelect,
  TechButton,
  TechButtonGhost,
  TechTabs,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatDelta,
  StatSub,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  listRecentNews,
  listScrapeRuns,
} from '../../services/competitorsService';
import { listMediaOutlets, listMediaArticles } from '../../services/mediaService';
import { getDeepSeekResponse } from '../../services/api/deepseek';
import CronCountdown from '../common/CronCountdown';

/* ---------- styled ---------- */

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  align-items: center;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) { flex-direction: column; align-items: stretch; }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;

  > svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-disabled);
    font-size: 0.85rem;
  }
  input { padding-left: 2rem; }
`;

const NewsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const NewsRow = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.6rem;
  padding: 0.6rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  font-size: 0.86rem;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.04);
  }

  a {
    color: var(--text-primary);
    text-decoration: none;
    line-height: 1.4;
    display: inline-flex;
    align-items: flex-start;
    gap: 6px;

    svg { color: var(--text-disabled); font-size: 0.75rem; margin-top: 4px; flex-shrink: 0; }

    &:hover { color: ${accent}; }
  }

  .meta {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    align-self: center;
    white-space: nowrap;
  }
`;

const OutletsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
`;

const OutletCard = styled.article`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 160px;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid ${borderSoft};

    h4 {
      margin: 0;
      font-size: 0.92rem;
      color: var(--text-primary);
      font-weight: 600;
    }
    .kind {
      font-family: ${mono};
      font-size: 0.55rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
    a {
      color: var(--text-disabled);
      font-size: 0.7rem;
      &:hover { color: ${accent}; }
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    flex: 1;
  }

  li {
    font-size: 0.78rem;
    line-height: 1.35;
    a {
      color: var(--text-secondary);
      text-decoration: none;
      &:hover { color: var(--text-primary); }
    }
    .when {
      display: block;
      font-family: ${mono};
      font-size: 0.58rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
      margin-top: 1px;
    }
  }

  footer {
    display: flex;
    justify-content: space-between;
    font-family: ${mono};
    font-size: 0.55rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    padding-top: 0.35rem;
    border-top: 1px solid ${borderSoft};
  }

  .empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-disabled);
    font-size: 0.78rem;
    font-style: italic;
  }
`;

const SaveBtn = styled.button`
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-disabled);
  width: 28px;
  height: 28px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover, &.active {
    color: ${accent};
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.08);
  }

  svg { font-size: 0.85rem; }
`;

const AIPanel = styled.section`
  position: relative;
  margin-top: 1.25rem;
  padding: 1.1rem 1.2rem;
  background:
    linear-gradient(180deg, rgba(0, 180, 216, 0.04), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const AIOutput = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
  padding: 0.95rem 1.1rem;
  color: var(--text-secondary);
  line-height: 1.55;
  font-size: 0.92rem;

  h2, h3 { color: var(--text-primary); margin: 0.7em 0 0.35em; font-weight: 600; }
  h3 { font-size: 0.95em; text-transform: uppercase; letter-spacing: 0.1em; color: ${accent}; }
  p { margin: 0 0 0.6em; }
  ul, ol { margin: 0 0 0.7em 1.2em; padding: 0; }
  strong { color: var(--text-primary); }
`;

const spinAnim = keyframes`to { transform: rotate(360deg); }`;
const SmallLoading = styled.div`
  display: flex; align-items: center; justify-content: center;
  gap: 0.55rem; padding: 1.5rem;
  color: var(--text-disabled); font-family: ${mono};
  font-size: 0.7rem; letter-spacing: 0.18em; text-transform: uppercase;
  &::before {
    content: ''; width: 12px; height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent}; border-radius: 50%;
    animation: ${spinAnim} 0.9s linear infinite;
  }
`;

/* ---------- helpers ---------- */

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  const days = h / 24;
  if (days < 7) return `hace ${Math.round(days)}d`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const SAVED_KEY = 'arcana_media_saved_v1';
const loadSaved = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || '[]')); } catch { return new Set(); }
};
const persistSaved = (s) => { try { localStorage.setItem(SAVED_KEY, JSON.stringify([...s])); } catch {} };

const TABS = [
  { id: 'overview', label: 'Resumen' },
  { id: 'news', label: 'Noticias' },
  { id: 'analysis', label: 'Análisis' },
  { id: 'saved', label: 'Guardados' },
];

const SORT_OPTIONS = [
  { id: 'date_desc', label: 'Más recientes' },
  { id: 'date_asc', label: 'Más antiguas' },
];

/* ---------- componente ---------- */

const MediaPresence = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [saved, setSaved] = useState(loadSaved);

  const [news, setNews] = useState([]);
  const [scrape, setScrape] = useState({ runs: [], totals: {}, schedule: null });
  const [outlets, setOutlets] = useState([]);
  const [outletArticles, setOutletArticles] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  const [aiState, setAiState] = useState({ loading: false, report: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setLoadState({ loading: true, error: null });
    Promise.all([
      listRecentNews({ limit: 100 }).catch(() => ({ news: [] })),
      listScrapeRuns(10).catch(() => ({ runs: [], totals: {}, schedule: null })),
      listMediaOutlets().catch(() => []),
      listMediaArticles({ limit: 80 }).catch(() => []),
    ])
      .then(([newsData, scrapeData, outletsData, articlesData]) => {
        if (cancelled) return;
        setNews(newsData?.news || []);
        setScrape(scrapeData || { runs: [], totals: {}, schedule: null });
        // /media solo muestra diarios/blogs/búsquedas; las redes viven en /social
        setOutlets((outletsData || []).filter((o) => o.kind !== 'red_social'));
        setOutletArticles(articlesData || []);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setLoadState({ loading: false, error: friendlyError(e, 'No se pudieron cargar los medios.') });
      });
    return () => { cancelled = true; };
  }, []);

  const toggleSaved = (id) => {
    setSaved((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      persistSaved(next);
      return next;
    });
  };

  const filtered = useMemo(() => {
    let list = [...news];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((n) =>
        (n.title || '').toLowerCase().includes(q) ||
        (n.source || '').toLowerCase().includes(q) ||
        (n.competitor_name || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const da = new Date(a.published_at || a.fetched_at).getTime() || 0;
      const db = new Date(b.published_at || b.fetched_at).getTime() || 0;
      return sort === 'date_asc' ? da - db : db - da;
    });
    return list;
  }, [news, search, sort]);

  const lastRun = scrape.runs?.[0];
  const totalArticles = scrape.totals?.articles ?? news.length;
  const competitors = scrape.totals?.competitors ?? 0;

  const summarize = async () => {
    setAiState({ loading: true, report: null, error: null });
    try {
      const sample = filtered.slice(0, 20).map((n) => ({
        titulo: n.title,
        fuente: n.source,
        competidor: n.competitor_name,
        fecha: n.published_at || n.fetched_at,
      }));
      const prompt = `Sos analista de medios. Tomá esta muestra real de noticias scrapeadas y devolvé en markdown:

## Síntesis
2-3 frases con el panorama mediático actual.

## Temas dominantes
Bullets con los 3-4 temas que más aparecen.

## Sentimiento general
1-2 oraciones describiendo el clima dominante.

## Acciones sugeridas
Lista numerada (3 ítems) de movimientos para esta semana.

Datos:
${JSON.stringify(sample, null, 2)}

Sé conciso y no copies titulares literales.`;
      const text = await getDeepSeekResponse({ prompt, max_tokens: 800, temperature: 0.5 });
      setAiState({ loading: false, report: text, error: null });
    } catch (err) {
      setAiState({ loading: false, report: null, error: friendlyError(err, 'No se pudo generar el resumen.') });
    }
  };

  const savedItems = filtered.filter((n) => saved.has(n.id));

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader
          title="Presencia en Medios"
          subtitle="Datos en vivo del monitoreo de medios"
          dossier="MED://STREAM/01"
        />
        <Loading>Cargando feed real</Loading>
      </TechShell>
    );
  }

  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Presencia en Medios" dossier="MED://STREAM/01" />
        <ErrorBox>No se pudo cargar el feed · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Presencia en Medios"
        subtitle="Diarios, blogs y agencias de noticias · monitoreo diario"
        dossier="MED://STREAM/01"
        actions={<TechButtonGhost type="button" onClick={() => window.location.reload()}><FaSyncAlt /> Refrescar</TechButtonGhost>}
      >
        <StatusPill>Feed <strong>activo</strong></StatusPill>
        <StatusPill>Última corrida <strong>{fmtRel(lastRun?.started_at)}</strong></StatusPill>
        <CronCountdown cronKey="media-outlets" label="Próxima actualización en" />
        <StatusPill>Guardados <strong>{saved.size}</strong></StatusPill>
      </TechPageHeader>

      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaNewspaper /> Notas indexadas</StatLabel>
          <StatValue>{totalArticles}</StatValue>
          <StatSub>{competitors} competidores</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaShareAlt /> En este feed</StatLabel>
          <StatValue>{news.length}</StatValue>
          <StatSub>últimas 100</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel>Notas última corrida</StatLabel>
          <StatValue>+{lastRun?.articles_added ?? 0}</StatValue>
          <StatSub>{lastRun?.competitors_processed ?? 0} procesados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaBookmark /> Guardados</StatLabel>
          <StatValue>{saved.size}</StatValue>
        </StatTile>
      </StatStrip>

      <FilterBar>
        <SearchWrap>
          <FaSearch />
          <TechInput
            placeholder="Buscar mención por medio, competidor o palabra clave…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrap>
        <TechSelect value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </TechSelect>
        <TechButtonGhost type="button"><FaFilter /> Más filtros</TechButtonGhost>
        <TechButton type="button"><FaPlus /> Nueva alerta</TechButton>
      </FilterBar>

      <TechTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'overview' && (
        <>
          <TechSection
            title={`Medios locales · ${outlets.filter((o) => o.active).length} fuentes fijas`}
            style={{ marginBottom: '1.25rem' }}
          >
            {outlets.length === 0 ? (
              <Empty>Sin medios configurados.</Empty>
            ) : (
              <OutletsGrid>
                {outlets.map((o) => {
                  // El endpoint /api/media/outlets ya incluye recent_articles (top 4).
                  // Fallback al filtro global por si la versión vieja del API responde.
                  const articles = Array.isArray(o.recent_articles) && o.recent_articles.length > 0
                    ? o.recent_articles
                    : outletArticles.filter((a) => a.outlet_slug === o.slug).slice(0, 4);
                  return (
                    <OutletCard key={o.id}>
                      <header>
                        <div>
                          <h4>{o.name}</h4>
                          <span className="kind">{o.kind} · {o.region || '—'}</span>
                        </div>
                        <a
                          href={o.home_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={o.home_url}
                        >
                          <FaExternalLinkAlt />
                        </a>
                      </header>
                      {articles.length === 0 ? (
                        <div className="empty">
                          {o.last_error ? `error · ${o.last_error.slice(0, 60)}` : 'sin notas todavía'}
                        </div>
                      ) : (
                        <ul>
                          {articles.map((a) => (
                            <li key={a.id}>
                              <a href={a.url} target="_blank" rel="noopener noreferrer">
                                {a.title}
                              </a>
                              <span className="when">
                                {fmtRel(a.published_at || a.fetched_at)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <footer>
                        <span>{o.articles_count || 0} notas</span>
                        <span>scrap: {fmtRel(o.last_scraped_at)}</span>
                      </footer>
                    </OutletCard>
                  );
                })}
              </OutletsGrid>
            )}
          </TechSection>

          <Grid>
            <Span span={8} className="mobile-full-width">
              <TechSection title="Tendencia de menciones · datos reales">
                <TrendChart news={news} />
              </TechSection>
            </Span>
            <Span span={4} className="mobile-full-width">
              <TechSection title="Distribución por fuente">
                <DistributionChart news={news} />
              </TechSection>
            </Span>
          </Grid>

          <TechSection
            title={`Últimas noticias · ${Math.min(15, filtered.length)} de ${filtered.length}`}
            style={{ marginTop: '1.25rem' }}
          >
            {filtered.length === 0 ? (
              <Empty>Sin noticias para esos filtros.</Empty>
            ) : (
              <NewsList>
                {filtered.slice(0, 15).map((n) => (
                  <NewsRow key={n.id}>
                    <a href={n.url} target="_blank" rel="noopener noreferrer">
                      {n.title} <FaExternalLinkAlt />
                    </a>
                    <span className="meta">
                      {n.competitor_name && (
                        <TechChip tone="mute" style={{ fontSize: '0.6rem', marginRight: 6, padding: '1px 6px' }}>
                          {n.competitor_name}
                        </TechChip>
                      )}
                      <span style={{ marginRight: 6 }}>{n.source}</span>
                      <FaClock style={{ marginRight: 3, verticalAlign: 'middle', fontSize: '0.7rem' }} />
                      {fmtRel(n.published_at || n.fetched_at)}
                      <SaveBtn
                        className={saved.has(n.id) ? 'active' : ''}
                        onClick={() => toggleSaved(n.id)}
                        style={{ marginLeft: 8, verticalAlign: 'middle' }}
                      >
                        <FaBookmark />
                      </SaveBtn>
                    </span>
                  </NewsRow>
                ))}
              </NewsList>
            )}
          </TechSection>

          <AIPanel>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${borderSoft}`, flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: mono,
                fontSize: '0.78rem',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <FaBrain style={{ color: accent }} /> Resumen IA del feed
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                <StatusPill>IA</StatusPill>
                <TechButton type="button" onClick={summarize} disabled={aiState.loading}>
                  <FaSyncAlt /> {aiState.loading ? 'Generando…' : aiState.report ? 'Regenerar' : 'Generar resumen'}
                </TechButton>
              </span>
            </header>
            {aiState.loading && <SmallLoading>Procesando feed</SmallLoading>}
            {aiState.error && <p style={{ color: 'var(--alert-error)', margin: 0 }}>No se pudo generar · {aiState.error}</p>}
            {!aiState.loading && !aiState.report && !aiState.error && (
              <p style={{ color: 'var(--text-disabled)', margin: 0, fontSize: '0.88rem' }}>
                Generá una síntesis IA sobre la cobertura mediática real (top 20 noticias) con temas dominantes, sentimiento y acciones sugeridas.
              </p>
            )}
            {aiState.report && (
              <AIOutput><ReactMarkdown>{aiState.report}</ReactMarkdown></AIOutput>
            )}
          </AIPanel>
        </>
      )}

      {activeTab === 'news' && (
        <TechSection
          title={`Noticias · ${filtered.length}`}
          action={<TechButtonGhost type="button"><FaSortAmountDown /> {SORT_OPTIONS.find((o) => o.id === sort)?.label}</TechButtonGhost>}
        >
          {filtered.length === 0 ? (
            <Empty>Sin noticias para esos filtros.</Empty>
          ) : (
            <NewsList>
              {filtered.map((n) => (
                <NewsRow key={n.id}>
                  <a href={n.url} target="_blank" rel="noopener noreferrer">
                    {n.title} <FaExternalLinkAlt />
                  </a>
                  <span className="meta">
                    {n.competitor_name && (
                      <TechChip tone="mute" style={{ fontSize: '0.6rem', marginRight: 6, padding: '1px 6px' }}>
                        {n.competitor_name}
                      </TechChip>
                    )}
                    <span style={{ marginRight: 6 }}>{n.source}</span>
                    {fmtRel(n.published_at || n.fetched_at)}
                    <SaveBtn
                      className={saved.has(n.id) ? 'active' : ''}
                      onClick={() => toggleSaved(n.id)}
                      style={{ marginLeft: 8, verticalAlign: 'middle' }}
                    >
                      <FaBookmark />
                    </SaveBtn>
                  </span>
                </NewsRow>
              ))}
            </NewsList>
          )}
        </TechSection>
      )}

      {activeTab === 'analysis' && (
        <Grid>
          <Span span={12}>
            <TechSection title="Tendencia integral">
              <TrendChart news={news} />
            </TechSection>
          </Span>
          <Span span={12}>
            <TechSection title="Distribución de fuentes">
              <DistributionChart news={news} />
            </TechSection>
          </Span>
        </Grid>
      )}

      {activeTab === 'saved' && (
        <TechSection title={`Guardados · ${savedItems.length}`}>
          {savedItems.length === 0 ? (
            <Empty>Aún no guardaste ningún ítem. Tocá el bookmark en cualquier noticia.</Empty>
          ) : (
            <NewsList>
              {savedItems.map((n) => (
                <NewsRow key={n.id}>
                  <a href={n.url} target="_blank" rel="noopener noreferrer">
                    {n.title} <FaExternalLinkAlt />
                  </a>
                  <span className="meta">
                    {n.competitor_name && (
                      <TechChip tone="mute" style={{ fontSize: '0.6rem', marginRight: 6, padding: '1px 6px' }}>
                        {n.competitor_name}
                      </TechChip>
                    )}
                    <span style={{ marginRight: 6 }}>{n.source}</span>
                    {fmtRel(n.published_at || n.fetched_at)}
                    <SaveBtn
                      className="active"
                      onClick={() => toggleSaved(n.id)}
                      style={{ marginLeft: 8, verticalAlign: 'middle' }}
                    >
                      <FaBookmark />
                    </SaveBtn>
                  </span>
                </NewsRow>
              ))}
            </NewsList>
          )}
        </TechSection>
      )}
    </TechShell>
  );
};

export default MediaPresence;
